import BoldText from '@/components/BoldText';
import ColorPickerCustom from '@/components/ColorPickerCustom';
import DropZoneCustom from '@/components/DropZoneCustom';
import { BackgroundColor, BackgroundType } from '@/constants/enum';
import { handleChangePreview, previewSelector } from '@/redux/slice/preview.slice';
import { validatePositiveIntegerInput } from '@/helpers/string';
import { BlockStack, Card, ChoiceList, Collapsible, Divider, Text, TextField } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import userPlans from '@/hooks/userPlans';
import CustomBadge from '@/components/CustomBadge';
import useLimitPlan from '@/hooks/useLimitPlan';

interface BackgroundSettingProps {
  setFiles: React.Dispatch<
    React.SetStateAction<{
      logo: File[];
      backgroundImage: File[];
    }>
  >;
}

const BackgroundSetting: React.FC<BackgroundSettingProps> = ({ setFiles }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['popup']);
  const previewDetail = useSelector(previewSelector);
  const { isUserFreeLimited, isUserFreeOrBasicLimited } = userPlans();
  const { isShowUpgradeNowRulePopup } = useLimitPlan();

  const choices = [
    { label: t('solid_color_background'), value: BackgroundType.BackgroundColor },
    { label: t('image_background'), value: BackgroundType.BackgroundImage },
    // { label: 'Color', value: BackgroundType.BackgroundColor },
  ];

  const handleChangeBackgroundType = (value: string[]) => {
    const isBackgroundColor = value[0] === BackgroundType.BackgroundColor;

    dispatch(
      handleChangePreview({
        mainBackgroundType: value[0] as BackgroundType,
        mainBackground: isBackgroundColor ? BackgroundColor.WhiteTransparent : '',
      }),
    );

    if (isBackgroundColor) {
      setFiles((prev) => ({
        ...prev,
        backgroundImage: [],
      }));
    }
  };

  const handleChangeTemplate = (key: keyof typeof previewDetail) => (value: string) => {
    if (key === 'popupBackgroundBorderWidth' || key === 'popupBackgroundBorderRadius') {
      value = validatePositiveIntegerInput(value);
    }
    dispatch(
      handleChangePreview({
        [key]: value,
      }),
    );
  };

  return (
    <BlockStack gap="200">
      <Card>
        <BoldText>{t('pop_up_background')}</BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <BlockStack gap="500">
          <ChoiceList
            choices={choices}
            selected={[previewDetail.mainBackgroundType]}
            title={
              <Text as="span" tone={isShowUpgradeNowRulePopup ? 'disabled' : 'base'}>
                {t('type')}
                {isShowUpgradeNowRulePopup && <CustomBadge />}
              </Text>
            }
            onChange={handleChangeBackgroundType}
            disabled={isShowUpgradeNowRulePopup}
          />

          <Collapsible
            id="bgColor-collapsible"
            open={previewDetail.mainBackgroundType === BackgroundType.BackgroundColor}
            expandOnPrint
            transition={{ duration: '300ms', timingFunction: 'ease-in-out' }}
          >
            <ColorPickerCustom
              onChange={handleChangeTemplate('mainBackground')}
              label={t('page_background_color')}
              color={previewDetail.mainBackground}
              keyColor="mainBackground"
              defaultColor={BackgroundColor.White}
              // requiredIndicator
              disabled={isShowUpgradeNowRulePopup}
              showBadge
            />
          </Collapsible>

          <Collapsible
            id="bgImage-collapsible"
            open={previewDetail.mainBackgroundType === BackgroundType.BackgroundImage}
            expandOnPrint
            transition={{ duration: '300ms', timingFunction: 'ease-in-out' }}
          >
            <DropZoneCustom
              fileDefault={
                previewDetail.mainBackgroundType === BackgroundType.BackgroundImage ? previewDetail.mainBackground : ''
              }
              label={
                <Text as="span" tone={isShowUpgradeNowRulePopup ? 'disabled' : 'base'}>
                  {t('page_background_image')}
                  {isShowUpgradeNowRulePopup && <CustomBadge />}
                </Text>
              }
              disabled={isShowUpgradeNowRulePopup}
              onChangeFile={(file, url) => {
                handleChangeTemplate('mainBackground')(url);
                setFiles((prev) => ({
                  ...prev,
                  backgroundImage: file,
                }));
              }}
              onRemove={() => {
                setFiles((prev) => ({
                  ...prev,
                  backgroundImage: [],
                }));
                dispatch(
                  handleChangePreview({
                    mainBackground: '',
                  }),
                );
              }}
            />
          </Collapsible>

          {previewDetail.mainBackgroundType !== BackgroundType.BackgroundImage && (
            <ColorPickerCustom
              onChange={handleChangeTemplate('popupBackground')}
              label={t('background_color')}
              color={previewDetail.popupBackground}
              keyColor="popupBackground"
              defaultColor={BackgroundColor.Black}
              disabled={isUserFreeLimited || isShowUpgradeNowRulePopup}
              showBadge
            />
          )}

          <DropZoneCustom
            disabled={isUserFreeLimited || isShowUpgradeNowRulePopup}
            fileDefault={previewDetail.logo}
            label={
              <Text as="span" tone={isUserFreeLimited || isShowUpgradeNowRulePopup ? 'disabled' : 'base'}>
                {t('logo_optional')} {(isUserFreeLimited || isShowUpgradeNowRulePopup) && <CustomBadge />}
              </Text>
            }
            onChangeFile={(file, url) => {
              handleChangeTemplate('logo')(url);
              setFiles((prev) => ({
                ...prev,
                logo: file,
              }));
            }}
            onRemove={() => {
              setFiles((prev) => ({
                ...prev,
                logo: [],
              }));
              dispatch(
                handleChangePreview({
                  logo: '',
                }),
              );
            }}
          />
        </BlockStack>
      </Card>

      <Card>
        <BoldText>
          {t('border_setting')}
          {isUserFreeOrBasicLimited && <CustomBadge />}
        </BoldText>
        <div className="mt-8 mb-8">
          <Divider />
        </div>
        <BlockStack gap="500">
          <ColorPickerCustom
            onChange={handleChangeTemplate('popupBackgroundBorderColor')}
            label={t('border_color')}
            color={previewDetail.popupBackgroundBorderColor}
            disabled={isUserFreeOrBasicLimited}
          />

          <TextField
            autoComplete=""
            label={<>{t('border_radius')}</>}
            value={previewDetail.popupBackgroundBorderRadius}
            onChange={handleChangeTemplate('popupBackgroundBorderRadius')}
            suffix="px"
            disabled={isUserFreeOrBasicLimited}
          />

          <TextField
            autoComplete=""
            label={<>{t('border_width')}</>}
            value={previewDetail.popupBackgroundBorderWidth}
            onChange={handleChangeTemplate('popupBackgroundBorderWidth')}
            suffix="px"
            disabled={isUserFreeOrBasicLimited}
          />
        </BlockStack>
      </Card>
    </BlockStack>
  );
};

export default BackgroundSetting;
